import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { Loader2, Sparkles, ChevronDown, ChevronUp } from "lucide-react";
import { TemplateSelector } from "@/components/admin/communication/TemplateSelector";
import { RichTextEditor } from "@/components/admin/RichTextEditor";
import { Textarea } from "@/components/ui/textarea";

export interface InquiryForEmail {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string | null;
  wedding_date?: string | null;
  venue?: string | null;
  services?: string[] | null;
  budget?: string | null;
  message?: string | null;
  selected_package?: string | null;
}

interface NewEmailComposeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inquiry?: InquiryForEmail | null;
  onEmailSent: () => void;
}

export function NewEmailComposeModal({
  open,
  onOpenChange,
  inquiry,
  onEmailSent,
}: NewEmailComposeModalProps) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [customInstructions, setCustomInstructions] = useState("");
  
  // For composing without an inquiry
  const [toEmail, setToEmail] = useState("");
  const [recipientName, setRecipientName] = useState("");
  
  // CC and BCC fields
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [showCcBcc, setShowCcBcc] = useState(false);

  useEffect(() => {
    if (open) {
      if (inquiry) {
        setToEmail(inquiry.email);
        setRecipientName(`${inquiry.first_name} ${inquiry.last_name}`.trim());
        setSubject(`Hello ${inquiry.first_name}!`);
      } else {
        setToEmail("");
        setRecipientName("");
        setSubject("");
      }
      setBody("");
      setCustomInstructions("");
      setCc("");
      setBcc("");
      setShowCcBcc(false);
    }
  }, [open, inquiry]);

  const handleTemplateSelect = (templateSubject: string, templateBody: string) => {
    setSubject(templateSubject);
    setBody(templateBody);
  };

  const handleGenerateReply = async () => {
    if (!inquiry) return;

    setGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-email-reply", {
        body: {
          inquiry: {
            first_name: inquiry.first_name,
            last_name: inquiry.last_name,
            email: inquiry.email,
            wedding_date: inquiry.wedding_date,
            venue: inquiry.venue,
            services: inquiry.services,
            budget: inquiry.budget,
            message: inquiry.message,
            selected_package: inquiry.selected_package,
          },
          customInstructions,
          isNewConversation: true,
        },
      });

      if (error) throw error;

      if (data?.reply) {
        setBody(data.reply);
        toast({ title: "AI reply generated" });
      }
    } catch (error) {
      console.error("Error generating reply:", error);
      toast({ title: "Failed to generate reply", variant: "destructive" });
    } finally {
      setGenerating(false);
    }
  };

  const handleSend = async () => {
    if (!toEmail.trim() || !body.trim()) {
      toast({ title: "Please enter recipient and message", variant: "destructive" });
      return;
    }

    setSending(true);
    try {
      let threadId: string;

      // Check for existing thread to avoid duplicates
      let existingThread = null;

      if (inquiry?.id) {
        // Look for thread linked to this inquiry
        const { data } = await supabase
          .from("email_threads")
          .select("id")
          .eq("inquiry_id", inquiry.id)
          .maybeSingle();
        existingThread = data;
      } else {
        // Look for thread by sender email (unlinked threads)
        const { data } = await supabase
          .from("email_threads")
          .select("id")
          .eq("sender_email", toEmail.toLowerCase())
          .is("inquiry_id", null)
          .maybeSingle();
        existingThread = data;
      }

      if (existingThread) {
        // Use existing thread
        threadId = existingThread.id;

        // Update thread timestamp and status
        await supabase
          .from("email_threads")
          .update({ 
            last_message_at: new Date().toISOString(),
            status: "open"
          })
          .eq("id", threadId);
      } else {
        // Create new thread
        const { data: newThread, error: threadError } = await supabase
          .from("email_threads")
          .insert({
            subject,
            sender_name: recipientName || toEmail,
            sender_email: toEmail.toLowerCase(),
            inquiry_id: inquiry?.id || null,
            status: "open",
            unread_count: 0,
          })
          .select()
          .single();

        if (threadError) throw threadError;
        threadId = newThread.id;
      }

      // Parse CC and BCC into arrays
      const ccList = cc.split(',').map(e => e.trim()).filter(e => e);
      const bccList = bcc.split(',').map(e => e.trim()).filter(e => e);

      // Send the email
      const { error: sendError } = await supabase.functions.invoke("send-email", {
        body: {
          threadId,
          toEmail,
          subject,
          bodyHtml: body,
          cc: ccList.length > 0 ? ccList : undefined,
          bcc: bccList.length > 0 ? bccList : undefined,
        },
      });

      if (sendError) throw sendError;

      toast({ title: "Email sent successfully" });
      onOpenChange(false);
      onEmailSent();
    } catch (error) {
      console.error("Error sending email:", error);
      toast({ title: "Failed to send email", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  // Build inquiry data for template selector (only if inquiry exists)
  const inquiryData = inquiry ? {
    first_name: inquiry.first_name,
    last_name: inquiry.last_name,
    email: inquiry.email,
    wedding_date: inquiry.wedding_date || null,
    venue: inquiry.venue || null,
    services: inquiry.services || null,
    budget: inquiry.budget || null,
    selected_package: inquiry.selected_package || null,
  } : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {inquiry ? `New Email to ${inquiry.first_name} ${inquiry.last_name}` : "New Email"}
          </DialogTitle>
          <DialogDescription>
            {inquiry ? "Start a new email conversation with this contact" : "Compose a new email"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* To field */}
          <div className="space-y-2">
            <Label>To</Label>
            {inquiry ? (
              <Input value={toEmail} disabled className="bg-muted" />
            ) : (
              <Input
                value={toEmail}
                onChange={(e) => setToEmail(e.target.value)}
                placeholder="recipient@example.com"
                type="email"
              />
            )}
          </div>

          {/* CC/BCC toggle and fields */}
          <div className="space-y-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowCcBcc(!showCcBcc)}
              className="text-muted-foreground h-auto p-0 hover:bg-transparent"
            >
              {showCcBcc ? (
                <ChevronUp className="h-4 w-4 mr-1" />
              ) : (
                <ChevronDown className="h-4 w-4 mr-1" />
              )}
              {showCcBcc ? "Hide CC/BCC" : "Add CC/BCC"}
            </Button>
            
            {showCcBcc && (
              <div className="space-y-3 pl-2 border-l-2 border-border">
                <div className="space-y-1">
                  <Label className="text-sm">CC</Label>
                  <Input
                    value={cc}
                    onChange={(e) => setCc(e.target.value)}
                    placeholder="email1@example.com, email2@example.com"
                    type="text"
                  />
                  <p className="text-xs text-muted-foreground">Separate multiple emails with commas</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">BCC</Label>
                  <Input
                    value={bcc}
                    onChange={(e) => setBcc(e.target.value)}
                    placeholder="email1@example.com, email2@example.com"
                    type="text"
                  />
                  <p className="text-xs text-muted-foreground">Recipients won't see BCC addresses</p>
                </div>
              </div>
            )}
          </div>

          {/* Recipient name field (only when no inquiry) */}
          {!inquiry && (
            <div className="space-y-2">
              <Label>Recipient Name (optional)</Label>
              <Input
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                placeholder="John Doe"
              />
            </div>
          )}

          {/* Subject field */}
          <div className="space-y-2">
            <Label>Subject</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject"
            />
          </div>

          {/* Template selector - only show if inquiry exists */}
          {inquiryData && (
            <div className="space-y-2">
              <Label>Use Template</Label>
              <TemplateSelector 
                inquiry={inquiryData} 
                onSelectTemplate={handleTemplateSelect} 
              />
            </div>
          )}

          {/* AI Assistant - only show if inquiry exists */}
          {inquiry && (
            <div className="space-y-2 p-4 border border-border rounded-lg bg-muted/30">
              <Label className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                AI Reply Assistant
              </Label>
              <Textarea
                placeholder="Custom instructions for AI (e.g., 'Focus on our premium packages', 'Mention our availability in June')"
                value={customInstructions}
                onChange={(e) => setCustomInstructions(e.target.value)}
                rows={2}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleGenerateReply}
                disabled={generating}
              >
                {generating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-4 w-4" />
                    Generate AI Reply
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Email body */}
          <div className="space-y-2">
            <Label>Message</Label>
            <RichTextEditor value={body} onChange={setBody} placeholder="Write your email..." />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={sending || !body.trim() || !toEmail.trim()}>
            {sending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              "Send Email"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
